/**
 * App 自然能力工具集
 * 包括网络状态处理、页面可见性处理、后台预加载、支付结果处理等
 */

import type { Router } from 'vue-router'
import { getSSEManager } from './sse'
import { showFancyToast } from './toast'
// 后台超时阈值（5分钟）
const BACKGROUND_TIMEOUT = 5 * 60 * 1000

// 后台时间记录
let backgroundTime: number | null = null

/**
 * 网络恢复处理
 */
export const handleOnline = async (stores: { appStore: any; chatStore: any }) => {
  console.log('🌐 网络已恢复')
  stores.appStore.setNetworkStatus(true)

  // 🔧 网络恢复后，检查 SSE 连接状态
  const sseManager = getSSEManager()
  sseManager.reconnectWithNewToken()

  // 🔧 网络恢复后延迟1秒补全断网期间丢失的消息
  setTimeout(async () => {
    await stores.chatStore.recoverMissingAIReplies()
  }, 1000)
}

/**
 * 网络断开处理
 */
export const handleOffline = (stores: { appStore: any; chatStore: any }) => {
  console.warn('🌐 网络已断开，清理所有异常状态')
  stores.appStore.setNetworkStatus(false)

  // 🔧 修复：网络断开时，清理所有 loading 状态，防止永久 loading
  stores.chatStore.clearAllDigitalHumanStates({
    loading: false,
    typing: false
  })
}

/**
 * 页面可见性变化处理
 */
export const handleVisibilityChange = (stores: { appStore: any }) => {
  if (document.hidden) {
    // 🔧 切入后台：记录时间
    backgroundTime = Date.now()
    stores.appStore.setPageVisibility(false)
    console.log('📱 应用切入后台，记录时间:', new Date(backgroundTime).toLocaleString())
  } else {
    // 🔧 回到前台：检查时间差
    stores.appStore.setPageVisibility(true)

    if (backgroundTime) {
      const timeInBackground = Date.now() - backgroundTime
      const minutes = Math.floor(timeInBackground / 60000)
      console.log(`📱 应用回到前台，后台时长: ${minutes} 分钟`)

      // 🔧 新增：检查 SSE 连接状态，如果断开则重连
      try {
        const sseManager = getSSEManager()
        const isConnected = sseManager.isReallyConnected()

        if (!isConnected) {
          console.log('🔌 检测到 SSE 连接断开，尝试重新连接...')
          sseManager.reconnectWithNewToken()
        } else {
          console.log('✅ SSE 连接正常')
        }
      } catch (error) {
        console.error('❌ 检查 SSE 连接状态失败:', error)
      }

      // 如果超过5分钟，刷新页面
      if (timeInBackground > BACKGROUND_TIMEOUT) {
        console.log('🔄 后台时长超过5分钟，刷新页面...')
        window.location.reload()
      }

      // 重置后台时间
      backgroundTime = null
    }
  }
}

/**
 * 后台预加载处理 - 主要预加载 mine 页面
 */
export const handlePreload = async (
  event: Event,
  stores: {
    userStore: any
    vipStore: any
    storePageStore: any
  },
  router: Router
) => {
  const customEvent = event as CustomEvent
  const { reason } = customEvent.detail


  try {
    // 1. 预加载 mine 页面数据
    await preloadMinePageData(stores)

    // 2. 预加载 mine 页面路由组件
    try {
      const resolved = router.resolve('/mine')
    } catch (err) {
      // console.warn('⚠️ 预加载 mine 路由失败:', err)
    }

    // console.log('✅ mine 页面后台预加载完成')
  } catch (error) {
    console.error('❌ mine 页面后台预加载失败:', error)
  }
}

/**
 * 预加载 mine 页面数据
 */
const preloadMinePageData = async (stores: {
  userStore: any
  vipStore: any
  storePageStore: any
}) => {
  // console.log('📦 开始预加载 mine 页面数据...')

  try {
    // 1. 预加载用户信息（mine 页面核心数据）
    if (stores.userStore.getStoredToken()) {
      if (!stores.userStore.userInfo?.userId) {
        await stores.userStore.getUserInfo()
        // console.log('✅ 用户信息预加载完成')
      } else {
        // console.log('✅ 用户信息已缓存')
      }

      // 2. 预加载 VIP 相关信息（通过 refreshVipPerks）
      try {
        if (!stores.vipStore.vipPerksInitialized) {
          await stores.vipStore.refreshVipPerks()
          // console.log('✅ VIP 权益信息预加载完成')
        } else {
          // console.log('✅ VIP 权益信息已缓存')
        }
      } catch (error) {
        console.warn('⚠️ VIP 权益信息预加载失败:', error)
      }

      // 3. 预加载充值项（mine 页面可能需要）
      try {
        if (stores.storePageStore.rechargeItems.length === 0) {
          await stores.storePageStore.fetchRechargeItems()
          // console.log('✅ 充值项预加载完成')
        } else {
          // console.log('✅ 充值项已缓存')
        }
      } catch (error) {
        console.warn('⚠️ 充值项预加载失败:', error)
      }
    } else {
      // console.log('⚠️ 未登录，跳过 mine 页面数据预加载')
    }

    // console.log('✅ mine 页面数据预加载完成')
  } catch (error) {
    console.warn('⚠️ mine 页面数据预加载失败:', error)
  }
}

/**
 * 支付结果处理
 */
export const handlePaymentResult = async (
  event: any,
  stores: {
    userStore: any
    vipStore: any
    storePageStore: any
  },
  icons: {
    successIcon: string
    errorIcon: string
  },
) => {

  try {
    if (event) {
      // 支付成功
      showFancyToast('You’re all set !', {
        subtitle:   `Gems have been added to your account.${event}`,
        icon: icons.successIcon,
        duration: 1000
      })
      // 刷新用户信息和充值项
      await stores.userStore.setIsSuccessfulPayment(false)
      await stores.userStore.getUserInfo()
      await stores.storePageStore.fetchRechargeItems()
    } else {
      showFancyToast('Not Completed', {
        subtitle: "Don’t worry, you haven’t been charged. Please try again in a moment.",
        icon: icons.errorIcon,
        duration: 1000,
      })
    }
  } catch (error) {
    console.error('❌ 支付结果处理错误:', error)
  }
}
